import { IntlShape } from 'react-intl';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import {
  CourseContent,
  TrainingContent,
  ProductContent,
  PanelContent,
  TypeNormalizer,
  CardStyle,
} from '../types';
import { stringOrUndefined, findNextOccasion } from '../utils';
import { messages } from '../messages';
import dateFormat from '@fhnw/components/DateFormat/dateFormat';
import { normalizeContent } from '../normalizeContent';

/**
 * Content types that should not display organisation entities
 * or universities in the card
 */
const NO_OE_TYPES = [
  'Document',
  'Location',
  'University',
  'Image',
  'File',
  'Link',
];

/**
 * Transforms base content
 */
export const normalizeBaseCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
) => {
  if (item?.['@type'] && NO_OE_TYPES.includes(item['@type'])) {
    return normalized;
  }

  const style: CardStyle = normalized?.style;

  if (
    university?.['@id'] &&
    item?.organisation_entities &&
    item.organisation_entities?.length > 0
  ) {
    if (style === 'tile') {
      normalized.tags = item.organisation_entities.map((e) => ({
        label: e.title,
      }));
    } else {
      normalized.info = [
        {
          key: intl.formatMessage(messages.Institution),
          value: stringOrUndefined(item.organisation_entities, intl),
        },
      ];
    }
  }

  if (!university?.['@id'] && item?.university_tokens) {
    const universitiesContent = Array.isArray(item.university_tokens)
      ? item.university_tokens
      : [item.university_tokens];
    if (style === 'tile') {
      normalized.tags = universitiesContent.map((u) => ({ label: u.title }));
    } else {
      normalized.info = [
        {
          key: intl.formatMessage(messages.School),
          value: stringOrUndefined(
            universitiesContent.map((u) => u.title),
            intl,
          ),
        },
      ];
    }
  }

  return normalized;
};

/**
 * Transforms school content (used for teaser cards)
 */
export const normalizeTeaserSchoolCard: TypeNormalizer = (
  item,
  normalized,
  intl,
) => {
  if (item?.nav_title) {
    normalized.title = item.nav_title;
  }

  return normalized;
};

/**
 * Transforms course-type content (DegreeProgram)
 */
export const normalizeCourseCard: TypeNormalizer = (item, normalized, intl) => {
  const courseItem = item as CourseContent;

  // Use product shortname or name as title if available
  if (courseItem?.product_shortname_without_type) {
    normalized.title = courseItem.product_shortname_without_type;
  } else if (courseItem?.product_name_without_type) {
    normalized.title = courseItem.product_name_without_type;
  }

  // Set category from degree program type
  if (courseItem?.degree_program_type) {
    normalized.category = stringOrUndefined(
      courseItem.degree_program_type,
      intl,
    );
  }

  // Build course information
  const info = buildCourseInfo(courseItem, intl, normalized?.style);
  if (info.length > 0) {
    normalized.info = info;
  }

  return normalized;
};

/**
 * Transforms training-type content (ContinuingEducation)
 */
export const normalizeTrainingCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
) => {
  const trainingItem = item as TrainingContent;
  const isPH: boolean = !!university?.['@id']?.match(/\/ph\/?$/);

  // Start with course transformation
  normalized = normalizeCourseCard(item, normalized, intl);

  // Override category with continuing education type
  if (!isPH && trainingItem?.continuing_education_type) {
    normalized.category = stringOrUndefined(
      trainingItem.continuing_education_type,
      intl,
    );
  } else if (isPH && trainingItem?.education_format) {
    normalized.category = stringOrUndefined(
      trainingItem.education_format,
      intl,
    );
  }

  // Add training-specific information
  const info = buildTrainingInfo(trainingItem, intl, normalized?.style, isPH);
  if (info.length > 0) {
    normalized.info = info;
  }

  return normalized;
};

/**
 * Transforms project-type content
 */
export const normalizeProjectCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
) => {
  normalized = normalizeBaseCard(item, normalized, intl, university);
  const style: CardStyle = normalized?.style;

  if (item?.research_area_tokens) {
    const researchAreaContent = Array.isArray(item.research_area_tokens)
      ? item.research_area_tokens
      : [item.research_area_tokens];
    if (style === 'tile') {
      normalized.tags = [
        ...(normalized?.tags || []),
        ...researchAreaContent.map((u) => ({ label: u.title })),
      ];
    } else {
      if (!normalized?.info) {
        normalized.info = [];
      }
      normalized.info.push({
        key: intl.formatMessage(messages.ResearchField),
        value: stringOrUndefined(
          researchAreaContent.map((u) => u.title),
          intl,
        ),
      });
    }
  }

  return normalized;
};

/**
 * Transforms product-type content
 */
export const normalizeProductCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
) => {
  normalized = normalizeBaseCard(item, normalized, intl, university);
  const productItem = item as ProductContent;

  // Use product name as title if available
  if (productItem?.product_name) {
    normalized.title = productItem.product_name;
  }

  return normalized;
};

/**
 * Transforms panel-type content (Specialization, ResearchFocus, Topic)
 */
export const normalizePanelCard: TypeNormalizer = (item, normalized, intl) => {
  const panelItem = item as PanelContent;

  // Remove description and image for panel cards
  delete normalized.description;
  delete normalized.image;

  // Set icon from panel_icon field
  if (panelItem?.panel_icon) {
    normalized.icon = normalizeContent(panelItem.panel_icon) as string;
  }

  return normalized;
};

/**
 * Builds course information section
 */
const buildCourseInfo = (
  item: CourseContent,
  intl: IntlShape,
  style?: CardStyle,
): InfoItemProps[] => {
  const info: InfoItemProps[] = [];

  if (style === 'tile' || style === 'list') {
    return info;
  }

  if (style === 'table') {
    // Degree program type
    if (item?.degree_program_type) {
      info.push({
        key: intl.formatMessage(messages.Type),
        value: stringOrUndefined(item.degree_program_type, intl),
      });
    }
  } else {
    // Study mode
    if (item?.study_mode) {
      info.push({
        key: intl.formatMessage(messages.StudyMode),
        value: stringOrUndefined(item.study_mode, intl),
      });
    }

    // Languages used
    if (item?.languages_used) {
      info.push({
        key: intl.formatMessage(messages.TeachingLanguage),
        value: stringOrUndefined(item.languages_used, intl),
      });
    }
  }

  // Location
  if (item?.location) {
    info.push({
      key: intl.formatMessage(messages.Venue),
      value: stringOrUndefined(item.location, intl),
    });
  }

  // Duration
  if (style !== 'table' && item?.duration) {
    info.push({
      key: intl.formatMessage(messages.Duration),
      value: stringOrUndefined(item.duration, intl),
    });
  }

  // Next start date
  let nextStart: string | null = null;
  const occasion = findNextOccasion(item?.occasions);

  if (occasion) {
    if (occasion?.occasion_start) {
      nextStart = dateFormat({
        start: new Date(occasion.occasion_start),
        dateOnly: true,
        locale: intl.locale,
      });
    } else if (occasion?.rolling_intake) {
      nextStart = intl.formatMessage(messages.RollingIntake);
    }
  }

  if (!nextStart && item?.next_start_fallback) {
    nextStart = stringOrUndefined(item.next_start_fallback, intl) || null;
  }

  if (nextStart) {
    info.push({
      key: intl.formatMessage(messages.NextStart),
      value: nextStart,
    });
  }

  return info;
};

/**
 * Builds training-specific information
 */
const buildTrainingInfo = (
  item: TrainingContent,
  intl: IntlShape,
  style?: CardStyle,
  isPH?: boolean,
): InfoItemProps[] => {
  let info: InfoItemProps[] = buildCourseInfo(item, intl, style);

  if (style === 'tile' || style === 'list') {
    return [];
  }

  // Fee
  if (item?.fee_int) {
    info.unshift({
      key: intl.formatMessage(messages.Fee),
      value: new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'CHF',
        trailingZeroDisplay: 'stripIfInteger',
      })
        .format(item.fee_int)
        .replaceAll('’', ' '),
    });
  }

  if (style === 'table') {
    // Continuing education type
    if (!isPH && item?.continuing_education_type) {
      info.unshift({
        key: intl.formatMessage(messages.Type),
        value: stringOrUndefined(item.continuing_education_type, intl),
      });
    }

    // Education format
    if (isPH && item?.education_format) {
      info.unshift({
        key: intl.formatMessage(messages.Format),
        value: stringOrUndefined(item.education_format, intl),
      });
    }
  } else {
    // Cost absorption
    if (isPH && item?.cost_absorption) {
      info.unshift({
        key: intl.formatMessage(messages.CostAbsorption),
        value: stringOrUndefined(item.cost_absorption, intl),
      });
    }
  }

  // Remove teaching language for PH trainings
  if (isPH) {
    info = info.filter(
      (item) => item.key !== intl.formatMessage(messages.TeachingLanguage),
    );

    // PH-spezifische Reihenfolge der Info-Items
    const PH_ORDER = [
      intl.formatMessage(messages.NextStart), // 1. Nächster Start
      intl.formatMessage(messages.Duration), // 2. Dauer
      intl.formatMessage(messages.Venue), // 3. Durchführungsort
      intl.formatMessage(messages.Fee), // 4. Preis
      intl.formatMessage(messages.CostAbsorption), // 5. Finanzierung (Kostenübernahme)
    ];

    info = info.sort((a, b) => {
      const aIndex = PH_ORDER.indexOf(a.key);
      const bIndex = PH_ORDER.indexOf(b.key);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  }

  return info;
};
